/**
 * Data service layer for Job Results & Dashboard
 *
 * Reads from Supabase via the JS client (uses user JWT → RLS enforced).
 * Status updates go through Supabase directly (RLS allows UPDATE on own rows).
 *
 * IMPORTANT: always pass explicit whitelists so internal/backend-only fields
 * never reach the browser.
 */

import { supabase, getAuthHeaders } from '../lib/supabase';

export async function downloadGeneratedCv(jobResultId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/generated-cv`, { headers });
  if (!response.ok) throw new Error('Could not download generated CV');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'generated-cv.html';
  anchor.click();
  URL.revokeObjectURL(url);
}
import { getSafeErrorMessage } from '../lib/apiErrors';

function apiError(body, fallbackContext, fallbackMessage, status) {
  const err = new Error(getSafeErrorMessage(body || fallbackMessage, { context: fallbackContext }));
  err.status = status;
  err.payload = body;
  return err;
}

function throwSupabaseError(error, context, fallbackMessage) {
  if (!error) return;
  const err = new Error(getSafeErrorMessage(error, { context }) || fallbackMessage);
  err.status = error.status || error.code;
  err.payload = { code: error.code, message: error.message };
  throw err;
}

// ── Public field whitelists ──────────────────────────────────────────
//
// Single source of truth for what the browser is allowed to read from
// Supabase. Anything not listed here stays server-side.

const JOB_RESULTS_PUBLIC_FIELDS = [
  // identity / structure
  'id', 'user_id', 'submission_id', 'run_id', 'rank', 'created_at',
  // listing core
  'job_title', 'company', 'location', 'salary', 'job_url',
  'archetype', 'listing_quality', 'legitimacy_note',
  // scoring
  'fit', 'total_score', 'role_score', 'skill_score', 'seniority_score', 'location_score',
  'role_reason', 'skill_reason', 'overall_reason',
  'top_keywords', 'missing_keywords', 'where_to_add',
  // CV-improvement preview (UI-only derivative fields, no internal generation details)
  'cv_improvement_suggestions', 'score_after_cv_improvements',
  'score_improvement_reason', 'projected_score_increase',
  // application tracking
  'application_status', 'response_status', 'response_status_updated_at',
  'applied_at', 'applied_via', 'response_stage', 'last_response_at',
  'next_followup_at', 'tracking_notes',
  // cover letter (artifact + UI metadata; internal generation details are NOT included)
  'cover_letter', 'cover_letter_subject', 'cover_letter_status',
  'cover_letter_error', 'cover_letter_generated_at', 'cover_letter_word_count',
  'cover_letter_quality_flags', 'cover_letter_model', 'cover_letter_language',
  'cover_letter_user_edits',
  // tailored CV (artifact + UI metadata; internal generation details are NOT included)
  'cv_status', 'cv_error', 'cv_generated_at', 'cv_model', 'cv_language',
  'cv_quality_flags', 'cv_unsupported_items_avoided', 'cv_changes_summary',
  'cv_section_counts', 'cv_original_style_profile',
  'tailored_cv_json', 'cv_user_edits_json',
  'user_edits_updated_at', 'cv_suggestion_decisions_json',
  // application HR email draft (artifact + UI metadata; internal generation details NOT included)
  'application_email_status', 'application_email_subject', 'application_email_body',
  'application_email_generated_at', 'application_email_error',
  'application_email_quality_flags', 'application_email_word_count',
  'application_email_attachment_recommendation',
  'application_email_user_edited_subject', 'application_email_user_edited_body',
  'application_email_user_edited_at',
].join(',');

const WORKFLOW_RUNS_PUBLIC_FIELDS = [
  'id', 'user_id', 'submission_id', 'status',
  'started_at', 'completed_at',
  'jobs_fetched', 'jobs_qualified', 'jobs_scored',
  'strong_count', 'medium_count', 'weak_count',
  'jobs_ai_errored', 'error_node', 'error_message', 'error_at',
  // supplemental "Run More Jobs" support
  'run_type', 'parent_run_id', 'source_group',
].join(',');

// ── Workflow Runs ─────────────────────────────────────────────────────

export async function fetchLatestCompletedRun() {
  // The "anchor" run on Dashboard / Job Results is always the latest completed
  // PRIMARY run. Supplemental "Run More Jobs" runs are merged into the primary
  // (see fetchJobResultsByRunId / fetchRunStats), never shown standalone.
  const { data, error } = await supabase
    .from('workflow_runs')
    .select(WORKFLOW_RUNS_PUBLIC_FIELDS)
    .eq('status', 'completed')
    .eq('run_type', 'primary')
    .order('completed_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') throwSupabaseError(error, 'editor-load', 'Could not load your latest run');
  return data || null;
}

export async function fetchAllRuns() {
  const { data, error } = await supabase
    .from('workflow_runs')
    .select(WORKFLOW_RUNS_PUBLIC_FIELDS)
    .order('started_at', { ascending: false });

  if (error) throwSupabaseError(error, 'editor-load', 'Could not load your runs');
  return Array.isArray(data) ? data : [];
}

// Returns supplemental run rows for a given primary run. Used to:
//  - aggregate job_results from primary + supplemental runs
//  - decide whether to show the "Run More Jobs" button (block while pending/running)
export async function fetchSupplementalRunsForParent(primaryRunId) {
  if (!primaryRunId) return [];
  const { data, error } = await supabase
    .from('workflow_runs')
    .select(WORKFLOW_RUNS_PUBLIC_FIELDS)
    .eq('parent_run_id', primaryRunId)
    .eq('run_type', 'supplemental')
    .order('started_at', { ascending: false });

  if (error) {
    throwSupabaseError(error, 'editor-load', 'Could not load supplemental runs');
  }
  return data || [];
}

// Resolve all run_ids whose job_results should be merged into a primary run's
// view: the primary run itself + every supplemental run that ran (or is running)
// against it. Failed supplementals are still included so partial results survive.
async function resolveMergedRunIds(primaryRunId) {
  const supplementals = await fetchSupplementalRunsForParent(primaryRunId);
  const ids = [primaryRunId, ...supplementals.map((r) => r.id)];
  // De-duplicate just in case
  return Array.from(new Set(ids));
}

// ── Run Stats (computed from job_results since metadata may be null) ──

export async function fetchRunStats(runId) {
  // Aggregate across the primary run + any supplemental "Run More Jobs" runs
  // attached to it. Supplemental rows append to the primary view, never replace.
  const runIds = await resolveMergedRunIds(runId);
  const { data, error } = await supabase
    .from('job_results')
    .select('id, fit')
    .in('run_id', runIds);

  if (error) {
    throwSupabaseError(error, 'editor-load', 'Could not load run statistics');
  }

  const rows = data || [];
  return {
    total: rows.length,
    strong: rows.filter((r) => r.fit === 'Strong').length,
    medium: rows.filter((r) => r.fit === 'Medium').length,
    weak: rows.filter((r) => r.fit === 'Weak').length,
    cvs: 0,
  };
}

// ── Application Pipeline ──────────────────────────────────────────────

export async function fetchApplicationPipeline(runId) {
  const runIds = await resolveMergedRunIds(runId);
  const { data, error } = await supabase
    .from('job_results')
    .select('application_status')
    .in('run_id', runIds);

  if (error) {
    throwSupabaseError(error, 'editor-load', 'Could not load application pipeline');
  }

  const counts = { to_apply: 0, applied: 0, interviewing: 0, offer: 0, rejected: 0 };
  (data || []).forEach((r) => {
    if (counts[r.application_status] !== undefined) counts[r.application_status]++;
  });
  return counts;
}

// ── Job Results ───────────────────────────────────────────────────────

export async function fetchJobResultsByRunId(runId) {
  // Merge job_results from the primary run AND every supplemental run whose
  // parent_run_id == runId. Primary results are NEVER hidden — supplementals
  // are appended and re-ranked together by score (rank may collide across runs).
  const runIds = await resolveMergedRunIds(runId);
  const { data, error } = await supabase
    .from('job_results')
    .select(JOB_RESULTS_PUBLIC_FIELDS)
    .in('run_id', runIds)
    .order('total_score', { ascending: false, nullsFirst: false })
    .order('rank', { ascending: true });

  if (error) {
    throwSupabaseError(error, 'editor-load', 'Could not load job results');
  }
  return data || [];
}

// ── Status Update ─────────────────────────────────────────────────────

export async function updateApplicationStatus(resultId, newStatus) {
  const { error } = await supabase
    .from('job_results')
    .update({ application_status: newStatus })
    .eq('id', resultId);

  if (error) {
    console.error('updateApplicationStatus error:', error);
    return { success: false, error };
  }
  return { success: true };
}

// ── Constants ─────────────────────────────────────────────────────────

export const APPLICATION_STATUSES = [
  { value: 'to_apply', label: 'To Apply' },
  { value: 'applied', label: 'Applied' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offer', label: 'Offer' },
  { value: 'rejected', label: 'Rejected' },
];

export const FIT_LEVELS = ['Strong', 'Medium', 'Weak'];

export const SCORE_MAX = 10;

// ── Cover Letter ──────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export async function requestCoverLetter({ job_result_id, force_regenerate = false, tone = 'professional', language = 'en', extra_notes = '' }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const resp = await fetch(`${BACKEND_URL}/api/generate-cover-letter`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ job_result_id, force_regenerate, tone, language, extra_notes }),
  });

  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'cover-letter', 'Failed to generate cover letter', resp.status);
  return body;
}

// ── Application HR Email Draft ────────────────────────────────────────
//
// Frontend calls backend only. Provider URLs and internal credentials stay server-side.
export async function requestApplicationEmailDraft({ job_result_id, force_regenerate = false, tone = 'direct', language = 'en', extra_notes = '' }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${job_result_id}/application-email-draft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ force_regenerate, tone, language, extra_notes }),
  });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  if (!resp.ok) {
    throw apiError(body, 'cover-letter', 'Failed to start HR email draft', resp.status);
  }
  return body;
}

export async function saveApplicationEmailDraftEdits({ job_result_id, subject, body }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${job_result_id}/application-email-draft`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ subject, body }),
  });
  let payload = null;
  try { payload = await resp.json(); } catch (_) { payload = null; }
  if (!resp.ok) {
    throw apiError(payload, 'editor-save', 'Failed to save HR email edits', resp.status);
  }
  return payload;
}

export async function fetchSingleJobResult(resultId) {
  const { data, error } = await supabase
    .from('job_results')
    .select(JOB_RESULTS_PUBLIC_FIELDS)
    .eq('id', resultId)
    .single();

  if (error) {
    throwSupabaseError(error, 'editor-load', 'Could not load job result');
  }
  return data;
}

// ── Tailored CV ───────────────────────────────────────────────────────

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    const err = new Error('Your session has expired. Please sign in again.');
    err.status = 401;
    throw err;
  }
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` };
}

// Dev-only structured logger. Logs endpoint URL / status / response body but
// NEVER tokens or request bodies. No-op in production.
function devLogApi(label, { url, status, body }) {
  if (process.env.NODE_ENV === 'production') return;
  try {
    // eslint-disable-next-line no-console
    console.debug('[api]', label, { url, status, body });
  } catch (_) { /* swallow */ }
}

export async function generateTailoredCv(jobResultId, { force_regenerate = false, target_language = 'auto', tone = 'professional', extra_notes = '' } = {}) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/generate-tailored-cv`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ job_result_id: jobResultId, force_regenerate, target_language, tone, extra_notes }),
  });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'tailored-cv', 'Failed to generate tailored CV', resp.status);
  return body;
}

export async function fetchTailoredCvStatus(jobResultId) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/cv-status`, { headers });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'tailored-cv', 'Failed to fetch CV status', resp.status);
  return body;
}

export async function fetchTailoredCvPreview(jobResultId) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/tailored-cv-preview/${jobResultId}`, { headers });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'cv-preview', 'Failed to fetch CV preview', resp.status);
  return body;
}

export async function downloadTailoredCvPdf(jobResultId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const resp = await fetch(`${BACKEND_URL}/api/tailored-cv-export/${jobResultId}/pdf`, {
    headers: { 'Authorization': `Bearer ${session.access_token}` },
  });
  if (!resp.ok) throw new Error('CV export is currently unavailable. Please try again after regenerating the CV.');
  const blob = await resp.blob();
  const disposition = resp.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `tailored-cv-${jobResultId}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadTailoredCvDocx(jobResultId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const resp = await fetch(`${BACKEND_URL}/api/tailored-cv-export/${jobResultId}/docx`, {
    headers: { 'Authorization': `Bearer ${session.access_token}` },
  });
  if (!resp.ok) throw new Error('CV export is currently unavailable. Please try again after regenerating the CV.');
  const blob = await resp.blob();
  const disposition = resp.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `tailored-cv-${jobResultId}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Editor (CV + Cover Letter) ────────────────────────────────────────

export async function fetchEditorData(jobResultId) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/editor-data`, { headers });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-load', 'Failed to load editor data', resp.status);
  return body;
}

export async function saveCvEdits(jobResultId, cvUserEditsJson) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/cv-edits`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ cv_user_edits_json: cvUserEditsJson }),
  });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-save', 'Failed to save CV edits', resp.status);
  return body;
}

export async function saveCvSuggestionDecisions(jobResultId, cvSuggestionDecisionsJson) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/cv-suggestion-decisions`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ cv_suggestion_decisions_json: cvSuggestionDecisionsJson }),
  });
  const body = await resp.json();
  if (!resp.ok) {
    throw apiError(body, 'editor-save', 'Failed to save CV suggestion decisions', resp.status);
  }
  return body;
}

export async function saveCoverLetterEdits(jobResultId, coverLetterText) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/cover-letter-edits`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ cover_letter_user_edits: coverLetterText }),
  });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-save', 'Failed to save cover letter edits', resp.status);
  return body;
}

// ── Response Status (Heard Back) ──────────────────────────────────────

export const RESPONSE_STATUSES = [
  { value: 'no_response', label: 'No Response' },
  { value: 'heard_back', label: 'Heard Back' },
];

export async function updateResponseStatus(jobResultId, responseStatus) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/response-status`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ response_status: responseStatus }),
  });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-save', 'Failed to update response status', resp.status);
  return body;
}

// ── Workflow run status polling ───────────────────────────────────────

export async function fetchWorkflowRunStatus(runId) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/workflow-runs/${runId}/status`, { headers });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-load', 'Failed to fetch run status', resp.status);
  return body;
}

// ── Application tracking ────────────────────────────────────────────

export const APPLIED_VIA_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'company_site', label: 'Company site' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

export const RESPONSE_STAGE_OPTIONS = [
  { value: 'no_response', label: 'No response' },
  { value: 'recruiter_screen', label: 'Recruiter screen' },
  { value: 'interview', label: 'Interview' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'offer', label: 'Offer' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

/**
 * Patch tracking fields on a job_result.
 * Pass only the fields you want to update. Strings → null clears the field.
 * Backend will auto-set applied_at + next_followup_at if status flipped to 'applied'
 * and those timestamps are missing.
 */
export async function updateTracking(jobResultId, patch) {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/job-results/${jobResultId}/tracking`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(patch),
  });
  const body = await resp.json();
  if (!resp.ok) throw apiError(body, 'editor-save', 'Failed to update tracking', resp.status);
  return body;
}

// ── Run More Jobs (supplemental search) ───────────────────────────────
//
// Calls the backend ONLY. Backend enforces:
//  - ownership (runs the JWT user owns)
//  - the primary run must be `completed`
//  - one supplemental per user per rolling 24h (HTTP 429)
//  - blocks if any supplemental for the user is pending/running (HTTP 409)
// Frontend just surfaces the resulting status / error to the user.
export async function triggerRunMoreJobs(primaryRunId) {
  const headers = await authHeaders();
  const resp = await fetch(
    `${BACKEND_URL}/api/workflow-runs/${primaryRunId}/run-more-jobs`,
    { method: 'POST', headers },
  );
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  if (!resp.ok) {
    throw apiError(body, 'find-jobs-submit', 'Failed to start supplemental search', resp.status);
  }
  return body;
}

// ── Master CV / Canonical CV review ─────────────────────────────────
//
// 3-step flow:
//   1) createSubmissionDraft(formData)   — POST /api/submissions/create-draft
//   2) fetchCanonicalCv(submissionId)    — GET  /api/submissions/{id}/canonical-cv
//      saveCanonicalCv(submissionId, …)  — PATCH /api/submissions/{id}/canonical-cv
//   3) confirmCvAndStartSearch(submissionId) — POST /api/submissions/{id}/confirm-cv-and-start-search
//      (the backend then starts the search workflow)
//
// Frontend only calls the backend. All bodies are surfaced as friendly errors.

export async function fetchCanonicalCv(submissionId) {
  const headers = await authHeaders();
  const resp = await fetch(
    `${BACKEND_URL}/api/submissions/${submissionId}/canonical-cv`,
    { headers },
  );
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  if (!resp.ok) {
    const err = new Error(getSafeErrorMessage(body, { context: 'cv-preview' }));
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function fetchLatestDraftSubmission() {
  const headers = await authHeaders();
  const resp = await fetch(`${BACKEND_URL}/api/submissions/latest-draft`, { headers });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  if (!resp.ok) {
    const err = new Error(getSafeErrorMessage(body, { context: 'find-jobs-submit' }));
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function saveCanonicalCv(submissionId, {
  canonical_cv_json,
  canonical_cv_text,
  cv_review_status,
  save_mode = 'explicit',
  expected_updated_at,
  force_overwrite = false,
  signal,
}) {
  const headers = await authHeaders();
  const url = `${BACKEND_URL}/api/submissions/${submissionId}/canonical-cv`;
  const payload = {
    canonical_cv_json,
    canonical_cv_text,
    save_mode,
    force_overwrite,
  };
  if (cv_review_status) payload.cv_review_status = cv_review_status;
  if (expected_updated_at) payload.expected_updated_at = expected_updated_at;
  const resp = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
    signal,
  });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  devLogApi('saveCanonicalCv', { url, status: resp.status, body });
  if (!resp.ok) {
    const err = new Error(getSafeErrorMessage(body, { context: 'editor-save' }));
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function confirmCvAndStartSearch(submissionId) {
  const headers = await authHeaders();
  const url = `${BACKEND_URL}/api/submissions/${submissionId}/confirm-cv-and-start-search`;
  const resp = await fetch(url, { method: 'POST', headers });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  devLogApi('confirmCvAndStartSearch', { url, status: resp.status, body });
  if (!resp.ok) {
    // 429 limit returns JSONResponse with code=search_limit_reached; surface that shape.
    const err = new Error(getSafeErrorMessage(body, { context: 'find-jobs-submit' }));
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body;
}

// PATCH draft job-search preferences before search starts. Backend rejects
// (typically HTTP 409) once any workflow_runs row exists for the submission.
// Caller surfaces the friendly "Search has already started…" message on 409.
export async function updateDraftPreferences(submissionId, payload) {
  const headers = await authHeaders();
  const url = `${BACKEND_URL}/api/submissions/${submissionId}/draft-preferences`;
  const resp = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {}),
  });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  devLogApi('updateDraftPreferences', { url, status: resp.status, body });
  if (!resp.ok) {
    const err = new Error(getSafeErrorMessage(body, { context: 'editor-save' }));
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body;
}
